import React from 'react';
import Icon from '../../../components/AppIcon';

// Badge styling per role — add entries here when new roles are added
const ROLE_BADGE = {
  'Fleet Manager': 'bg-primary/10 text-primary',
  'Yard Manager': 'bg-secondary/10 text-secondary'
};

const formatSuppressionReason = (row) => {
  if (!row) return '';
  const reasonLabel = row?.reason === 'hard_bounce' ? 'Hard bounce'
    : row?.reason === 'soft_bounce' ? 'Soft bounce (3+ failures)'
    : row?.reason === 'complaint' ? 'Spam complaint'
    : row?.reason;
  const date = row?.first_seen_at
    ? new Date(row.first_seen_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;
  return `${reasonLabel}${date ? ` · since ${date}` : ''}`;
};

const FleetContactsTable = ({ contacts, onEdit, onDelete, filterYardId, suppressions = {} }) => {
  if (contacts?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
          <Icon name="Users" size={32} color="var(--color-muted-foreground)" strokeWidth={2} />
        </div>
        <p className="text-sm font-medium text-foreground mb-1">No fleet contacts found</p>
        <p className="text-xs text-muted-foreground">
          {filterYardId ? 'No contacts are assigned to this yard.' : 'Add a contact to get started.'}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead className="bg-muted/50 sticky top-0 z-10">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Name</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Phone</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Email</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Role</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-foreground">Yards</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {contacts?.map((contact) => {
            // Prefer new role column; fall back to contactfleetmgr for any un-migrated rows
            const roleLabel = contact?.role
              ? contact?.role
              : contact?.contactfleetmgr
                ? 'Fleet Manager' :'Yard Manager';

            const badgeClass = ROLE_BADGE?.[roleLabel] || 'bg-muted text-muted-foreground';
            const isFleetMgr = roleLabel === 'Fleet Manager';

            // Check suppression: use email_bad flag from DB, or cross-reference suppressions map
            const emailKey = contact?.email?.toLowerCase()?.trim();
            const isBouncing = contact?.email_bad === true || (emailKey && !!suppressions?.[emailKey]);
            const suppressionRow = emailKey ? suppressions?.[emailKey] : null;

            return (
              <tr key={contact?.id} className="hover:bg-muted/30 transition-hover">
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground">{contact?.name || '—'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground font-mono">{contact?.phone || '—'}</span>
                </td>
                <td className="px-4 py-3">
                  {contact?.email ? (
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">{contact?.email}</span>
                      {isBouncing && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/20 w-fit"
                          title={suppressionRow ? formatSuppressionReason(suppressionRow) : 'This address has been flagged as invalid or bouncing.'}
                        >
                          <Icon name="AlertTriangle" size={11} strokeWidth={2.5} />
                          Email bouncing
                          {suppressionRow && (
                            <span className="font-normal text-destructive/70 ml-1">· {formatSuppressionReason(suppressionRow)}</span>
                          )}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${badgeClass}`}>
                    {roleLabel}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {isFleetMgr && contact?.assignedYards?.length > 0 ? (
                      contact?.assignedYards?.map(yard => (
                        <span
                          key={yard?.id}
                          className="inline-flex items-center px-2 py-0.5 text-xs rounded-md bg-secondary/10 text-secondary font-medium"
                        >
                          {yard?.name}
                        </span>
                      ))
                    ) : contact?.yardName ? (
                      <span className="text-sm text-muted-foreground">{contact?.yardName}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end space-x-1">
                    <button
                      onClick={() => onEdit(contact)}
                      className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded transition-hover"
                      title="Edit contact"
                    >
                      <Icon name="Edit2" size={16} strokeWidth={2} />
                    </button>
                    <button
                      onClick={() => onDelete(contact)}
                      className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-hover"
                      title="Delete contact"
                    >
                      <Icon name="Trash2" size={16} strokeWidth={2} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default FleetContactsTable;
